import {Page} from '@PosterWhiteboard/page/page.class';
import type {PosterPageObject} from '@PosterWhiteboard/poster/poster.types';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import type {DeepPartial} from '@/global';
import {noop} from '@Utils/general.util';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {FabricObject} from '@postermywall/fabricjs-2';

interface AddPageOpts {
  undoable?: boolean;
  updateRedux?: boolean;
  pageOrder?: number;
  onItemAddFail?: () => void;
  immediatelySelectOnceAdded?: boolean;
}

export class PosterPages {
  public pagesHashMap: Record<string, Page> = {};
  public pageOrder: string[] = [];
  public isPageSwiping = false;
  public poster: Poster;

  public constructor(poster: Poster) {
    this.poster = poster;
  }

  public async addPage(
    pageObject: DeepPartial<PageObject> = {},
    opts: AddPageOpts = {}
  ): Promise<Page> {
    if (this.poster.isUpdatingFromObject()) {
      throw new Error('Cannot add page while poster is updating from object');
    }
    return this.#doAddPage(pageObject, opts);
  }

  async #doAddPage(
    pageObject: DeepPartial<PageObject> = {},
    {updateRedux = true, undoable = true, pageOrder, onItemAddFail = noop, immediatelySelectOnceAdded = false}: AddPageOpts = {}
  ): Promise<Page> {
    const page = new Page(this.poster, {
      hashedID: pageObject.hashedID,
      order: pageOrder,
      zoom: pageObject.zoom,
    });
    this.pagesHashMap[page.hashedID] = page;

    const filteredPageOrder = this.pageOrder.filter((id) => {
      return id !== page.hashedID;
    });
    if (pageOrder !== undefined && pageOrder <= filteredPageOrder.length) {
      filteredPageOrder.splice(pageOrder, 0, page.hashedID);
      this.pageOrder = filteredPageOrder;
    } else {
      this.pageOrder = [...filteredPageOrder, page.hashedID];
    }

    if (immediatelySelectOnceAdded) {
      page.poster.setCurrentPage(page.hashedID);
    }

    pageObject.zoom = this.poster.scaling.scale;
    await page.updateFromObject(pageObject, {undoable: false, updateRedux: false, onItemAddFail});

    this.initSelectionEvents(page);

    this.poster.observePage(page);
    this.poster.updatePageSuspensionStates();

    if (undoable) {
      this.poster.history.addPosterHistory();
    }

    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }

    return page;
  }

  private initSelectionEvents(newlyAddedPage: Page): void {
    newlyAddedPage.fabricCanvas.on('selection:created', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });

    newlyAddedPage.fabricCanvas.on('mouse:down', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });

    newlyAddedPage.fabricCanvas.on('selection:updated', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });
  }

  public movePageUp(pageId: string): void {
    if (this.poster.isUpdatingFromObject()) {
      return;
    }
    this.movePageToIndex(pageId, this.pageOrder.indexOf(pageId) - 1);
  }

  public movePageDown(pageId: string): void {
    if (this.poster.isUpdatingFromObject()) {
      return;
    }
    this.movePageToIndex(pageId, this.pageOrder.indexOf(pageId) + 1);
  }

  public movePageToIndex(pageId: string, newIndex: number): void {
    if (this.poster.isUpdatingFromObject()) {
      return;
    }
    const newPagesOrder = [...this.pageOrder];
    const oldIndex = this.pageOrder.indexOf(pageId);
    newPagesOrder.splice(oldIndex, 1);
    newPagesOrder.splice(newIndex, 0, pageId);

    this.pageOrder = newPagesOrder;
    this.refreshPagesHtmlOrder();
    this.poster.updateCurrentPageBasedOnVisibility();

    this.poster.history.addPosterHistory();
    this.poster.redux.updateReduxData();
  }

  public toObject(): PosterPageObject {
    const pageObjects: Record<string, PageObject> = {};

    for (const [key, page] of Object.entries(this.pagesHashMap)) {
      pageObjects[key] = page.toObject();
    }

    return {
      pagesHashMap: pageObjects,
      pageOrder: this.pageOrder,
    };
  }

  public getTotalDuration(): number {
    let totalDuration = 0;
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      totalDuration += page.getDuration();
    }
    return totalDuration;
  }

  public refreshPagesHtmlOrder(): void {
    const parent = document.querySelector('.pmw-page-container-parent');

    if (parent) {
      const containerMap = new Map<string, HTMLElement>();

      const containers = parent.querySelectorAll<HTMLElement>('.pmw-page-container');
      containers.forEach((container) => {
        const dataId = container.getAttribute('data-id');
        if (dataId) {
          containerMap.set(dataId, container);
        }
      });

      this.pageOrder.forEach((id) => {
        const el = containerMap.get(id);
        if (el) {
          parent.appendChild(el);
        }
      });
      this.poster.updateCurrentPageBasedOnVisibility();
      this.poster.refreshAllVisibleCanvases();
    }
  }

  public setZoom(zoom: number, doRefreshVisibleCanvas = true): void {
    for (const [, page] of Object.entries(this.poster.pages.pagesHashMap)) {
      page.setZoom(zoom, doRefreshVisibleCanvas);
    }
  }

  public async updateFromObject(
    posterPagesObject: DeepPartial<PosterPageObject>,
    {updateRedux = true, fireModifiedEvent = true, undoable = true, refreshActiveSelection = false, onItemAddFail = noop, pageIdsToUpdate}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    if (!posterPagesObject.pagesHashMap) {
      return;
    }

    // Update/Add pages from pagesObject
    const promises = [];
    for (const [hashId, pageObject] of Object.entries(posterPagesObject.pagesHashMap)) {
      if (pageIdsToUpdate && !pageIdsToUpdate.includes(hashId)) {
        continue;
      }
      if (this.pagesHashMap[hashId] !== undefined) {
        promises.push(
          this.pagesHashMap[hashId].updateFromObject(pageObject, {
            updateRedux: false,
            fireModifiedEvent: true,
            firePosterModifiedEvent: false,
            undoable: false,
            refreshActiveSelection,
            onItemAddFail,
          })
        );
      } else {
        promises.push(this.#doAddPage(pageObject, {updateRedux: false, undoable: false, pageOrder: posterPagesObject.pageOrder?.indexOf(hashId), onItemAddFail}));
      }
    }

    // Delete pages that are in the poster but not in the pagesObject.
    // Note: pageIdsToUpdate intentionally does not filter here — page deletions must always be applied
    // regardless of which pages were changed in the diff, otherwise removed pages linger on canvas.
    for (const [hashId] of Object.entries(this.pagesHashMap)) {
      if (posterPagesObject.pagesHashMap[hashId] === undefined) {
        promises.push(this.doRemovePage(hashId));
      }
    }
    await Promise.all(promises);

    if (posterPagesObject.pageOrder) {
      this.pageOrder = [...posterPagesObject.pageOrder];
      this.refreshPagesHtmlOrder();
    }

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.poster.fire('modified');
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
  }

  public async removePage(pageUid: string): Promise<void> {
    if (this.poster.isUpdatingFromObject()) {
      return;
    }
    if (this.hasPage(pageUid)) {
      await this.doRemovePage(pageUid);
      this.poster.fire('modified');
      this.poster.history.addPosterHistory();
      this.poster.redux.updateReduxData();
      this.poster.refreshAllVisibleCanvases();
    }
  }

  public hasPage(hashId: string): boolean {
    return this.pagesHashMap[hashId] !== undefined;
  }

  public getPage(hashId: string): Page | undefined {
    if (!this.hasPage(hashId)) {
      return undefined;
    }

    return this.pagesHashMap[hashId];
  }

  public getNextPage(): Page | undefined {
    const currentPageId = this.poster.getCurrentPageId();
    const currentIndex = this.pageOrder.indexOf(currentPageId);
    const nextPageId = currentIndex < this.pageOrder.length - 1 ? this.pageOrder[currentIndex + 1] : null;
    return nextPageId ? this.getPage(nextPageId) : undefined;
  }

  public getPreviousPage(): Page | undefined {
    const currentPageId = this.poster.getCurrentPageId();
    const currentIndex = this.pageOrder.indexOf(currentPageId);
    const prevPageId = currentIndex > 0 ? this.pageOrder[currentIndex - 1] : null;
    return prevPageId ? this.getPage(prevPageId) : undefined;
  }

  public getItemForId(uid: string): ItemType | undefined {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.items.hasItem(uid)) {
        return page.items.getItem(uid);
      }
    }
    return undefined;
  }

  public getPageWithItemId(uid: string): Page | undefined {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.items.hasItem(uid)) {
        return page;
      }
    }
    return undefined;
  }

  public hasMultiplePages(): boolean {
    return Object.keys(this.pagesHashMap).length > 1;
  }

  public isPremium(): boolean {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.isPremium()) {
        return true;
      }
    }

    return false;
  }

  public async pause(): Promise<void> {
    const promises = [];
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      promises.push(page.pause());
    }
    await Promise.all(promises);
  }

  public async stop(): Promise<void> {
    const promises = [];
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      promises.push(page.stop());
    }
    await Promise.all(promises);
  }

  public clearSelection(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.clearSelection();
    }
  }

  public dispose(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.dispose().catch(console.error);
    }
  }

  private async doRemovePage(hashId: string): Promise<void> {
    if (this.hasPage(hashId)) {
      this.poster.unobservePage(this.pagesHashMap[hashId]);
      await this.pagesHashMap[hashId].dispose();
      const newCurrentPageIdIfCurrentPageDeleted = this.getAdjacentPageIdOnPageRemoved(this.poster.getCurrentPageId());
      // this.page.fabricCanvas.remove(this.pagesHashMap[itemUid].fabricObject);
      delete this.pagesHashMap[hashId];
      this.pageOrder = this.pageOrder.filter((id) => {
        return id !== hashId;
      });
      // setCurrentPage only updates activePageId; clear selectedItemPageId here (without dispatching
      // Redux yet — setCurrentPage below will do that) so getCurrentPage() doesn't keep returning
      // the just-deleted page when it was the selected one.
      if (this.poster.selectedItemPageId === hashId) {
        this.poster.selectedItemPageId = undefined;
      }
      if (this.poster.getCurrentPageId() === hashId) {
        this.poster.setCurrentPage(newCurrentPageIdIfCurrentPageDeleted);
        this.pagesHashMap[newCurrentPageIdIfCurrentPageDeleted]?.refreshVisibleCanvas();
      }
    }
  }

  public getAdjacentPageIdOnPageRemoved(hashId: string): string {
    const pageOrder = this.pageOrder.indexOf(hashId);
    const adjacentPageOrder = pageOrder === 0 ? 1 : pageOrder - 1;
    return this.pageOrder[adjacentPageOrder];
  }

  public getPageOrder(pageHashId: string): number | undefined {
    const pageOrder = this.pageOrder.indexOf(pageHashId);
    return pageOrder !== -1 ? pageOrder : undefined;
  }

  public getPagesInOrder(): Page[] {
    const pages = [];
    for (const pageId of this.pageOrder) {
      pages.push(this.pagesHashMap[pageId]);
    }

    return pages;
  }

  public getNumberOfPages(): number {
    return Object.keys(this.pagesHashMap).length;
  }

  public getStartTimeForPage(pageId: string): number {
    let startTime = 0;
    for (const id of this.pageOrder) {
      if (pageId === id) {
        return startTime;
      }
      startTime += this.pagesHashMap[id].getDuration();
    }
    throw new Error(`No page in pageOrder for id ${pageId}`);
  }

  public findPageContainingItem(fabricObject: FabricObject): Page | null {
    for (const page of Object.values(this.pagesHashMap)) {
      if (getPosterUi().isFabricObjectOnPageCanvas(fabricObject, page.hashedID)) {
        return page;
      }
    }

    return null;
  }

  public getEndTimeForPage(pageId: string): number {
    return this.getStartTimeForPage(pageId) + this.pagesHashMap[pageId].getDuration();
  }
}
